import { useState } from 'react';

import { Grid } from '@mui/material';

import useDownloadQuery from 'hooks/useDownloadQuery/useDownloadQuery';
import { ApiMethods, CuredApiPaths, DashboardNames, DownloadQueryKeys } from 'models/enums';

import DashboardDownloadSnackbar from '../DashboardDownloadSnackbar';
import DashboardHeader from '../DashboardHeader';
import { sixMonthsAgo, today } from '../filtering/DateFilter/DateFilter';
import { FiltersForApiState, FilterValuesState } from '../filtering/types';
import { getTimeframeForApi } from '../filtering/utils';

import ActionsTakenCard from './ActionsTakenCard';
import CallDurationDensityOverTimeCard from './CallDurationDensityOverTimeCard';
import CallsAttemptedAnsweredOverTimeCard from './CallsAttemptedAnsweredOverTimeCard';
import CallVolumeOverTimeCard from './CallVolumeOverTimeCard';
import Filters from './Filters';
import OptOutRateOverTimeCard from './OptOutRateOverTimeCard';
import VoiceCallMetricsBySendCard from './VoiceCallMetricsBySendCard';
import VoiceStatsCards from './VoiceStatsCards';

const VoiceDashboard = () => {
    const end = today;
    const start = sixMonthsAgo;
    // This state value is tracking the input values of the fields used for filtering.
    const [filterValues, setFilterValues] = useState<FilterValuesState>({
        selectedSendsValue: [],
        filterTimeframe: { start, end },
        filterRelativeTimeframe: {
            direction: null,
            duration: null,
            unit: null,
        },
    });
    const [isRelativeTimeframe, setIsRelativeTimeframe] = useState(false);

    const campaignsForApi = filterValues.selectedCurationsValue?.map((curation) => curation?.campaign_id);
    const timeframeForApi = getTimeframeForApi(isRelativeTimeframe, filterValues);

    // This state value is tracking what's sent to the API for filtering, which may not
    // match the field values as filters are not applied until a button is clicked.
    const [filtersForApi, setFiltersForApi] = useState<FiltersForApiState>({
        campaigns: campaignsForApi,
        timeframe: timeframeForApi,
    });

    const { downloadFile, isDownloading, isSuccess, error, resetDownloadState } = useDownloadQuery(
        DownloadQueryKeys.VOICE_DASHBOARD,
        CuredApiPaths.REPORT_VOICE_DOWNLOAD,
        ApiMethods.POST
    );

    // This handles clicking on the button to apply filters by setting the filtersForApi
    // state to match the input state values.
    const handleApplyFilters = () => {
        setFiltersForApi({
            campaigns: campaignsForApi,
            timeframe: timeframeForApi,
        });
    };

    // This function will handle the API call to get the downloadable report according to the
    // filters applied in the current state.
    const handleDownloadClick = () => {
        downloadFile({ filtersForApi: filtersForApi });
    };
    return (
        <div style={{ paddingBottom: '32px' }}>
            <DashboardDownloadSnackbar
                isDownloading={isDownloading}
                isSuccess={isSuccess}
                error={error}
                dashboardName={DashboardNames.VOICE}
                onSuccessClosed={resetDownloadState}
                onErrorClosed={resetDownloadState}
            />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Filters
                        filterValues={filterValues}
                        handleApplyFilters={handleApplyFilters}
                        isRelativeTimeframe={isRelativeTimeframe}
                        setFilterValues={setFilterValues}
                        setIsRelativeTimeframe={setIsRelativeTimeframe}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DashboardHeader
                        title="voice call"
                        subtitle="take action with cured insights"
                        description="Filter on specific voice call Curations to quickly identify what's working to improve future sends."
                        handleDownloadClick={handleDownloadClick}
                    />
                </Grid>
                <VoiceStatsCards filtersForApi={filtersForApi} />
                <Grid item xs={12} md={6}>
                    <ActionsTakenCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CallDurationDensityOverTimeCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CallVolumeOverTimeCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <OptOutRateOverTimeCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12}>
                    <CallsAttemptedAnsweredOverTimeCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12}>
                    <VoiceCallMetricsBySendCard filtersForApi={filtersForApi} />
                </Grid>
            </Grid>
        </div>
    );
};

export default VoiceDashboard;
